import { AnyBlock, ImageBlock, Point2D, Size } from "../types";
import AWS from "aws-sdk";
import { v4 as uuidv4 } from "uuid";
import {
  clampPosition,
  pixelToGridPosition,
  pixelToGridSize,
} from "./gridPosition";
import {
  MAX_IMG_WIDTH,
  MAX_IMG_HEIGHT,
  MAX_IMG_RESOLUTION_WIDTH,
  MAX_IMG_RESOLUTION_HEIGHT,
  DEFAULT_GRID_RESOLUTION,
} from "../consts";
import * as StackBlur from "stackblur-canvas";
import fitRect from "fit-rect";
import createBlock from "../blocks/createBlock";

const bucketRegion = "us-east-1";
const accessId = "AKIAULKSLQGYVDJMSB45";
const secret = "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";

export const albumBucketName = "makespace1";
export const AWSConfig = {
  region: bucketRegion,
  credentials: {
    accessKeyId: accessId,
    secretAccessKey: secret,
  },
};

export function uploadImage(blob: Blob) {
  // Upload to S3
  // TODO: get getSignedUrl method to work
  AWS.config.update(AWSConfig);
  return new AWS.S3.ManagedUpload({
    params: {
      Bucket: albumBucketName,
      Key: "pastes/" + uuidv4(),
      Body: blob,
      ACL: "public-read",
      ContentType: blob.type,
    },
  }).promise();
}

export function handleScreenshot(
  blob: Blob,
  upsertBlock: (b: AnyBlock) => void,
  blockPosition: Point2D,
  sizePixels?: Size
) {
  uploadImage(blob).then(
    (data) => {
      const imageUrl = data.Location;
      const imageObj = new Image();
      imageObj.addEventListener("load", function (e: any) {
        const w = sizePixels?.width || this.width;
        const h = sizePixels?.height || this.height;
        const block: ImageBlock = createBlock(
          "image",
          clampPosition(blockPosition),

          pixelToGridSize(
            {
              width: w,
              height: h,
            },
            true
          )
        ) as ImageBlock;
        if (!block) return;
        block.sticker = true;
        if (block) upsertBlock({ ...block, value: imageUrl });
      });
      imageObj.src = imageUrl;
    },
    (err) => {
      console.log("error uploading image :( ", err);
    }
  );
}

export function createPreviewDataURL(img: HTMLImageElement) {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    return;
  }

  const aspect = img.width / img.height;

  canvas.width = 32 * aspect;
  canvas.height = 32;

  context.drawImage(img, 0, 0, canvas.width, canvas.height);
  StackBlur.canvasRGBA(canvas, 0, 0, canvas.width, canvas.height, 3);

  return canvas.toDataURL();
}

export function capImageSize(img: HTMLImageElement, file: File): Promise<Blob> {
  // skip resizing for gifs, they stop being animated with the current method
  const isGif = file.type === "image/gif";
  if (isGif) {
    return Promise.resolve(file);
  }

  if (
    img.width < MAX_IMG_RESOLUTION_WIDTH &&
    img.height < MAX_IMG_RESOLUTION_HEIGHT
  ) {
    // no need to resize, upload as is
    return Promise.resolve(file);
  }

  const rect = [0, 0, img.width, img.height];
  const target = [0, 0, MAX_IMG_RESOLUTION_WIDTH, MAX_IMG_RESOLUTION_HEIGHT];
  const contained = fitRect(rect, target, "contain");

  const targetWidth = contained[2];
  const targetHeight = contained[3];

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    return Promise.reject();
  }

  canvas.width = targetWidth;
  canvas.height = targetHeight;
  context.drawImage(img, 0, 0, targetWidth, targetHeight);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject();
      }
    });
  });
}

export function handlePaste(
  dataTransfer: DataTransfer | null,
  upsertBlock: (b: Partial<AnyBlock>) => void,
  mousePosition: Point2D
) {
  const items = dataTransfer?.items;
  if (!items) return;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!item.type.includes("image")) continue;

    const file = item.getAsFile();
    if (!file) continue;

    const image = new Image();

    image.addEventListener("load", function () {
      // Cap  size of inserted image block
      // (Image resolution is capped seperately)
      const rect = [0, 0, this.width, this.height];
      const target = [0, 0, MAX_IMG_WIDTH, MAX_IMG_HEIGHT];
      const contained = fitRect(rect, target, "contain");

      const size = pixelToGridSize(
        { width: contained[2], height: contained[3] },
        true
      );

      const block = createBlock(
        "image",
        clampPosition(
          pixelToGridPosition({ x: mousePosition.x, y: mousePosition.y })
        ),
        size
      );

      const value = createPreviewDataURL(image);

      if (block) upsertBlock({ ...block, size, value });

      capImageSize(image, file)
        .then(uploadImage)
        .then((data) => {
          const imageUrl = data.Location;
          if (block)
            upsertBlock({
              id: block.id,
              value: imageUrl,
            });
        })
        .catch((err) => {
          console.log("error uploading image :( ", err);
        });
    });

    image.src = URL.createObjectURL(file);
  }
}

export function handleDroppedImage(
  width: any,
  height: any,
  mousePositionRef: React.MutableRefObject<Point2D>,
  url: any,
  upsertBlock: (block: Partial<AnyBlock>) => void
) {
  let aspectRatio = width / height;
  let gridSize = pixelToGridSize({
    width: width,
    height: height,
  });
  gridSize.width = Math.min(8, gridSize.width);
  gridSize.height = gridSize.width / aspectRatio;

  const block = createBlock(
    "image",

    clampPosition(
      pixelToGridPosition({
        x:
          mousePositionRef.current.x -
          (gridSize.width * DEFAULT_GRID_RESOLUTION) / 2,
        y:
          mousePositionRef.current.y -
          (gridSize.height * DEFAULT_GRID_RESOLUTION) / 2,
      })
    ),
    gridSize,
    url
  );
  if (block) {
    upsertBlock(block);
  }
}
